import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Spinner } from '@wartek-id/spinner'
import { Shimmer } from '@wartek-id/shimmer'

import SemiCircularProgress from 'app/KinerjaShared/components/SemiCircularProgress'
import { Header } from 'app/KinerjaShared/components/Header'
import { PANDUAN_PENGELOLAAN_URL } from 'app/KinerjaShared/utils/panduan'
import { PENGELOLAAN_KINERJA_PS_PATH } from 'app/KinerjaShared/constants/path'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import usePenetapanCountPs from 'app/KinerjaPSPage/hooks/usePenetapanCountPs'

import type { FC } from 'react'

interface PsPenetapanIndexProps {}

const LoadingBlock = () => {
  return (
    <div className="p-6 border border-subdued rounded-md flex flex-row items-center gap-9">
      <div
        className={clsx('w-56 h-36', {
          'flex items-center justify-center': true,
        })}
      >
        <Spinner size="lg" />
      </div>

      <div className="flex-1 flex flex-col items-start h-full">
        <Shimmer width={259} height={22} />
        <Shimmer width={413} height={22} className="mt-1" />
        <div className="flex flex-row items-center gap-4 mt-4">
          <Shimmer width={87} height={46} />
          <Shimmer width={345} height={22} />
        </div>
      </div>
    </div>
  )
}

const PsPenetapanIndex: FC<PsPenetapanIndexProps> = ({}) => {
  const {
    isLoading: isLoadingPengawasProfile,
    profilePs: pengawasProfile,
    isPengawasSekolah,
    leave,
  } = useRoleContext()
  if (!isLoadingPengawasProfile && !isPengawasSekolah) leave()

  const { isLoading: isLoadingPenilaianCount, data: counter } =
    usePenetapanCountPs({
      enabled: !!pengawasProfile,
    })

  const countTotal = counter?.all || 0
  const countSent = counter?.sudahDikirim || 0
  let percentage = 0
  if (countSent === 0 && countSent === countTotal) {
    percentage = 0
  } else if (countSent >= countTotal) {
    percentage = 100
  } else if (countSent < countTotal) {
    percentage = (countSent / countTotal) * 100
  }

  return (
    <div>
      <Header
        title="Penetapan Rekomendasi Predikat Kinerja"
        className="print:hidden"
      />

      {/* Card: Pelajari lebih lanjut ... */}
      <section className="bg-white mt-8 rounded flex flex-row gap-4 p-3">
        <Image
          alt=""
          width={84}
          height={84}
          layout="fixed"
          src="/images/ujikom/img-doc-file-graphic-chart.png"
        />
        <div className="flex flex-col gap-1 items-start">
          <Text variant="action">
            Pelajari lebih lanjut tentang Penetapan Predikat Kinerja
          </Text>
          <Text variant="body" color="subdued">
            Berisi tentang pandangan substansi dan regulasi terkait Predikat
            Kinerja dan sebagainya.
          </Text>
          <Link href={PANDUAN_PENGELOLAAN_URL} passHref>
            <Button as="a" color="white" size="sm">
              Baca selengkapnya
            </Button>
          </Link>
        </div>
      </section>

      {/* Card: Langkah-langkah Penetapan ... */}
      <section className="bg-white mt-6 rounded-md p-6">
        <Text variant="heading-md">
          Langkah-langkah Penetapan Rekomendasi Predikat Kinerja
        </Text>

        <div className="flex flex-col gap-4 mt-4 mx-2.5">
          {isLoadingPenilaianCount ? (
            <LoadingBlock />
          ) : (
            <div
              className={clsx(
                'p-6',
                'border border-subdued rounded-md',
                'flex flex-row items-center gap-9'
              )}
            >
              <div
                className={clsx('w-56 h-36', {
                  'flex items-center justify-center': isLoadingPenilaianCount,
                })}
              >
                <SemiCircularProgress value={percentage}>
                  <div className={clsx('flex flex-col items-center', 'mb-12')}>
                    <Text
                      variant="heading-lg"
                      color="informational"
                      className="!text-blue-60"
                    >
                      {counter?.sudahDikirim || 0}/{counter?.all || 0}
                    </Text>
                    <Text variant="body" color="disabled">
                      Sudah dikirim
                    </Text>
                  </div>
                </SemiCircularProgress>
              </div>

              <div className="flex-1">
                <Text variant="heading-sm">
                  Kirim Rekomendasi Predikat Kinerja ke Dinas Pendidikan
                </Text>
                <Text variant="body" color="subdued" className="mt-1">
                  Rekomendasi sudah otomatis terisi berdasarkan penilaian Anda
                  di tahap sebelumnya. Setelah dikirim, Dinas Pendidikan akan
                  memeriksa dan melanjutkan proses.
                </Text>

                {counter?.belumDikirim >= 1 ? (
                  <div className="flex flex-row items-center gap-4 mt-4">
                    <Link
                      href={`${PENGELOLAAN_KINERJA_PS_PATH}/penetapan/kirim`}
                      passHref
                    >
                      <Button as="a" color="blue" size="lg">
                        Mulai
                      </Button>
                    </Link>

                    <Text
                      variant="body"
                      color="informational"
                      className="!text-blue-60"
                    >
                      Ada Rekomendasi Predikat Kinerja yang dapat dikirim
                      sekarang
                    </Text>
                  </div>
                ) : (
                  <div className="flex flex-row items-center gap-4 mt-4">
                    <Link
                      href={`${PENGELOLAAN_KINERJA_PS_PATH}/penetapan/kirim`}
                      passHref
                    >
                      <Button as="a" color="white" size="lg">
                        Cek
                      </Button>
                    </Link>

                    <Text variant="body" color="disabled">
                      Belum ada Predikat Kinerja yang dapat dikirim sekarang
                    </Text>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-center">
            <Icon className="!cursor-default">arrow_downward</Icon>
          </div>

          {isLoadingPenilaianCount ? (
            <LoadingBlock />
          ) : (
            <div
              className={clsx(
                'p-6',
                'border border-subdued rounded-md',
                'flex flex-row items-center gap-9'
              )}
            >
              <Image
                alt=""
                height={84}
                width={84}
                src={'/illustrations/img-collect-files-folder.png'}
              />

              <div className="flex-1">
                <Text variant="heading-sm">Seluruh tahapan selesai</Text>
                <Text variant="body" color="subdued" className="mt-1">
                  Dinas Pendidikan akan menerima rekomendasi dan melanjutkan
                  proses.
                </Text>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default PsPenetapanIndex
